import { Controller } from "@hotwired/stimulus"

// Client-side cost calculator for the "Calculator cost blog: freelancer vs. Contento"
// free tool. Compares the monthly cost of three ways of producing SEO articles —
// a freelancer/agency, writing them yourself (with ChatGPT), and Contento — as the
// visitor drags the sliders. Pure math, no server round-trip.
export default class extends Controller {
  static values = {
    // Contento's flat monthly price (lei), passed from the view so it stays in
    // sync with the real pricing. See app/models/subscription.rb.
    contentoPrice: Number,
    // Articles Contento includes per month at that price.
    contentoArticles: Number,
    // Hours a founder realistically spends per DIY article (research + write +
    // optimise + publish) and total oversight hours/month with Contento.
    diyHoursPerArticle: Number,
    contentoHoursMonthly: Number,
    // Monthly ChatGPT Plus cost (lei) assumed for the DIY scenario.
    chatgptMonthly: Number
  }

  static targets = [
    "articlesSlider", "articlesLabel",
    "priceSlider", "priceLabel",
    "rateSlider", "rateLabel",
    "freelancerTotal", "freelancerPerArticle",
    "diyTotal", "diyHours",
    "contentoTotal", "contentoPerArticle",
    "savings", "savingsAnnual", "hoursSaved"
  ]

  connect() {
    this.update()
  }

  disconnect() {
    // No listeners/timers to tear down — Stimulus manages the data-action bindings.
  }

  update() {
    const articles = parseInt(this.articlesSliderTarget.value, 10)
    const pricePerArticle = parseInt(this.priceSliderTarget.value, 10)
    const hourlyRate = parseInt(this.rateSliderTarget.value, 10)

    // Slider labels
    this.articlesLabelTarget.textContent = `${articles} ${articles === 1 ? "articol" : "articole"} / lună`
    this.priceLabelTarget.textContent = `${this.formatLei(pricePerArticle)} lei / articol`
    this.rateLabelTarget.textContent = `${this.formatLei(hourlyRate)} lei / oră`

    // Scenario 1 — Freelancer / agenție
    const freelancerTotal = articles * pricePerArticle
    this.freelancerTotalTarget.textContent = `${this.formatLei(freelancerTotal)} lei`
    this.freelancerPerArticleTarget.textContent = `${this.formatLei(pricePerArticle)} lei / articol`

    // Scenario 2 — Scrii singur (tu + ChatGPT). Costul real e timpul tău.
    const diyHours = articles * this.diyHoursPerArticleValue
    const diyTotal = diyHours * hourlyRate + this.chatgptMonthlyValue
    this.diyTotalTarget.textContent = `${this.formatLei(diyTotal)} lei`
    this.diyHoursTarget.textContent = `${diyHours} ore din timpul tău / lună`

    // Scenario 3 — Contento (preț fix, până la N articole incluse)
    const contentoTotal = this.contentoPriceValue
    const contentoPerArticle = articles > 0 ? Math.round(contentoTotal / articles) : contentoTotal
    this.contentoTotalTarget.textContent = `${this.formatLei(contentoTotal)} lei`
    this.contentoPerArticleTarget.textContent = `≈ ${this.formatLei(contentoPerArticle)} lei / articol`

    // Economii — vs. cea mai scumpă alternativă realistă (freelancer)
    const savings = freelancerTotal - contentoTotal
    const hoursSaved = diyHours - this.contentoHoursMonthlyValue

    this.savingsTarget.textContent = `${this.formatLei(Math.max(savings, 0))} lei / lună`
    this.savingsAnnualTarget.textContent = `${this.formatLei(Math.max(savings, 0) * 12)} lei / an`
    this.hoursSavedTarget.textContent = `${Math.max(hoursSaved, 0)} ore / lună`
  }

  formatLei(value) {
    // Romanian thousands separator (e.g. 3000 -> "3.000").
    return Math.round(value).toLocaleString("ro-RO")
  }
}
